// Deterministic mock-row generators for the lists-at-scale demo (00-ds-09, D148).
// Seeded so every reload shows the same hundreds/thousands of rows. Real data
// comes from the server index (§8); this only exists so the demo can actually
// page, search and filter.

import { MOCK_NOW } from './query';

/** mulberry32 — a tiny deterministic PRNG. */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function pick<T>(r: () => number, arr: readonly T[]): T {
  const idx = Math.min(arr.length - 1, Math.floor(r() * arr.length));
  return arr[idx] as T;
}
export function int(r: () => number, min: number, max: number): number {
  return Math.floor(r() * (max - min + 1)) + min;
}
/** A dollar amount, 2 decimals, in [min, max]. */
export function money(r: () => number, min: number, max: number): number {
  return Math.round((r() * (max - min) + min) * 100) / 100;
}

export const FIRST_NAMES = [
  'James', 'Sana', 'Tomás', 'Ethan', 'Olivia', 'Marcus', 'Priya', 'Noah', 'Mia', 'Liam',
  'Sofia', 'Aisha', 'Victor', 'Emma', 'Lucas', 'Hana', 'Diego', 'Grace', 'Omar', 'Chloe',
  'Ava', 'Elijah', 'Zoe', 'Mateo', 'Nina', 'Caleb', 'Leah', 'Ivan', 'Rosa', 'Theo',
] as const;
export const LAST_NAMES = [
  'Whitfield', 'Malik', 'Vega', 'Brooks', 'Carter', 'Lee', 'Nair', 'Reyes', 'Chen', 'Patel',
  'Martinez', 'Okafor', 'Nguyen', 'Johnson', 'Rossi', 'Kim', 'Alvarez', 'Walsh', 'Haddad', 'Flores',
] as const;
export const PAYERS = [
  'Aetna', 'Delta Dental', 'Cigna', 'MetLife', 'UnitedHealthcare', 'Guardian', 'Sun Life', 'BCBS of Texas', 'Humana',
] as const;
export const CDT = [
  { code: 'D2740', desc: 'Crown, porcelain' },
  { code: 'D2950', desc: 'Core buildup' },
  { code: 'D1110', desc: 'Adult cleaning' },
  { code: 'D0220', desc: 'Periapical X-ray' },
  { code: 'D4341', desc: 'Scaling and root planing' },
  { code: 'D7140', desc: 'Extraction' },
  { code: 'D2392', desc: 'Resin, two surfaces' },
  { code: 'D3330', desc: 'Root canal, molar' },
] as const;

export function fullName(r: () => number): string {
  return `${pick(r, FIRST_NAMES)} ${pick(r, LAST_NAMES)}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A date up to `monthsBack` months before MOCK_NOW. Returns both ISO + a US label. */
export function pastDate(r: () => number, monthsBack: number): { iso: string; label: string } {
  const d = new Date(MOCK_NOW);
  d.setDate(d.getDate() - int(r, 0, Math.round(monthsBack * 30.4)));
  const iso = d.toISOString().slice(0, 10);
  const label = `${MONTHS[d.getMonth()] ?? 'Jan'} ${String(d.getDate())}, ${String(d.getFullYear())}`;
  return { iso, label };
}

/** Build `count` rows deterministically; `factory(r, i)` uses the seeded rng. */
export function generate<T>(count: number, seed: number, factory: (r: () => number, i: number) => T): T[] {
  const r = rng(seed);
  return Array.from({ length: count }, (_, i) => factory(r, i));
}

/** "$2,422.40" */
export function usd(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
/** "$2,422" (whole dollars) */
export function usdWhole(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}
